import { DestroyRef, inject, Injectable, NgZone } from '@angular/core'
import { takeUntilDestroyed } from '@angular/core/rxjs-interop'
import { Router } from '@angular/router'
import { AuthEventType } from '@ftr/api-shared'
import { AppName } from '@ftr/contracts/api/standalone-apps'
import { UserSettings } from '@ftr/contracts/api/user'
import { Shortcut, SHORTCUT_DEFAULTS, ShortcutMap, ShortcutMapEntries } from '@ftr/contracts/type/desktop'
import { ONE_HOUR_MS } from '@ftr/contracts/type/shared'
import { isInDesktopApp, unwrapData } from '@ftr/foundation'
import { LaunchDarklyService } from '@ftr/ui-feature-flags'
import { AuthenticationService } from '@ftr/ui-user'
import { WindowRefService } from '@ftr/window'
import { BehaviorSubject, combineLatest, from, Observable, Subject, switchMap, tap, timer } from 'rxjs'
import { compare } from 'semver'
import { mapAcceleratorsPlatformSpecificModifiers } from '../../utils'
import { StandaloneAppsCoreApiService } from '../standalone-apps-core-api'

@Injectable({
  providedIn: 'root',
})
export class DesktopApiService {
  private shortcut = new Subject<Shortcut>()
  private updateReady = new BehaviorSubject<boolean | undefined>(undefined)
  private updateRequired = new BehaviorSubject<boolean>(false)

  #destroyRef = inject(DestroyRef)
  authenticationService = inject(AuthenticationService)
  windowRefService = inject(WindowRefService)
  standaloneAppsCoreApiService = inject(StandaloneAppsCoreApiService)
  router = inject(Router)
  launchDarklyService = inject(LaunchDarklyService)
  zone = inject(NgZone)

  initialize(): void {
    if (!isInDesktopApp()) {
      return
    }

    this.authenticationService.authEvents.subscribe(authEvent => {
      if (authEvent.type === AuthEventType.Login && authEvent.user?.id) {
        this.windowRefService.getDesktopElectronApi()?.registerUserId?.(authEvent.user?.id)
      }
    })

    this.windowRefService.getDesktopElectronApi()?.handleShortcutPressed?.(shortcut => {
      this.shortcut.next(shortcut)
    })

    this.windowRefService.getDesktopElectronApi()?.handleUpdateReady?.(isMajorUpdate => {
      this.updateReady.next(isMajorUpdate)
    })

    this.windowRefService.getDesktopElectronApi()?.handleNavigate?.(path => {
      this.zone.run(() => {
        void this.router.navigateByUrl(path)
      })
    })

    this.setupAppVersionCheck()
  }

  private setupAppVersionCheck(): void {
    const initialDesktopVersionPromise = this.windowRefService.getDesktopElectronApi()?.getVersionInfo?.()
    if (initialDesktopVersionPromise === undefined) {
      return
    }

    initialDesktopVersionPromise.then(initialDesktopVersion => {
      timer(0, ONE_HOUR_MS)
        .pipe(
          takeUntilDestroyed(this.#destroyRef),
          switchMap(() =>
            combineLatest({
              flagEnabled: this.launchDarklyService.observeFlagChanges<boolean>('enable-desktop-web-version-checks'),
              appVersionInfo: from(this.windowRefService.getDesktopElectronApi()!.getVersionInfo!()),
              manifestVersion: this.standaloneAppsCoreApiService
                .getLatestVersion(AppName.Desktop, initialDesktopVersion.appPlatform)
                .pipe(unwrapData()),
            }),
          ),
          tap(async ({ flagEnabled, appVersionInfo, manifestVersion }) => {
            if (!flagEnabled) {
              return
            }

            if (appVersionInfo.updateInProgress) {
              return
            }

            // TODO: DI-3179 - enable this once the EOL policy has been figured out
            // if (hasMajorUpdate(manifestVersion.version, appVersionInfo.version)) {
            //   await this.router.navigate(['/', AppPaths.DesktopUpdateSplash])
            //   return
            // }

            if (isHigherVersion(manifestVersion.version, appVersionInfo.version)) {
              this.updateRequired.next(true)
              return
            }
          }),
        )
        .subscribe()
    })
  }

  getShortcutAsObservable(): Observable<Shortcut> {
    return this.shortcut.asObservable()
  }

  getUpdateRequiredAsObservable(): Observable<boolean> {
    return this.updateRequired.asObservable()
  }

  getUpdateReadyAsObservable(): Observable<boolean | undefined> {
    return this.updateReady.asObservable()
  }

  setOnPlaybackPage(onPlaybackPage: boolean): void {
    this.windowRefService.getDesktopElectronApi()?.onPlaybackPage?.(onPlaybackPage)
  }

  getDefaultShortcuts(): ShortcutMap {
    return new Map(
      mapAcceleratorsPlatformSpecificModifiers([...SHORTCUT_DEFAULTS.entries()], this.windowRefService.isMac()),
    )
  }

  getUserShortcuts(userSettings: UserSettings): ShortcutMap {
    return new Map([...this.getDefaultShortcuts().entries(), ...this.getCurrentPlatformCustomShortcuts(userSettings)])
  }

  getUserShortcutsForSettings(shortcuts: ShortcutMap): Partial<UserSettings> {
    const customShortcuts = Array.from(shortcuts.entries())
    return this.windowRefService.isMac()
      ? { customShortcutsMac: customShortcuts }
      : { customShortcutsWindows: customShortcuts }
  }

  registerShortcuts(shortcuts: ShortcutMap): Promise<Shortcut[]> {
    return this.windowRefService.getDesktopElectronApi()?.registerShortcuts?.(shortcuts) ?? Promise.resolve([])
  }

  async checkShortcutBinding(shortcuts: ShortcutMap): Promise<Shortcut[]> {
    const failed = await this.registerShortcuts(shortcuts)
    // Clear out the shortcuts we tested the binding for
    await this.registerShortcuts(new Map())
    return failed
  }

  private getCurrentPlatformCustomShortcuts(userSettings: UserSettings): ShortcutMapEntries {
    return this.windowRefService.isMac() ? userSettings.customShortcutsMac : (userSettings.customShortcutsWindows ?? [])
  }
}

// TODO: DI-3179 - enable this once the EOL policy has been figured out
// function hasMajorUpdate(v1: string, v2: string): boolean {
//   return major(v1) > major(v2)
// }

function isHigherVersion(v1: string, v2: string): boolean {
  return compare(v1, v2) === 1
}
